// B1/B2 (Blok 1, 2026-05-15) — "Logged but not invoiced" card. Two modes,
// switched by the backend `kind` discriminator:
//   - 'hourly'  → non-Hour-Package project. Summary + per-month list of
//                 approved/submitted entries that are not on any invoice.
//                 Per-row Generate button navigates to /invoices/new with
//                 a prefilled month.
//   - 'overage' → SLA Hour Package project with overLimitAllowed. Per-month
//                 list of overage hours (FIFO over each month) with a
//                 per-role expand, PM approval chip, and a top zero-rate
//                 banner when any overage hour cannot be billed because the
//                 role lacks a ProjectOverageRate.
//
// Card hides itself for SLA Hour Package projects without overLimitAllowed
// (backend returns empty payload, so byMonth.length === 0 short-circuits).
// The To Be Invoiced card owns the paushal invoice surface either way.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../ui/Card';
import { useProjectUninvoicedHours } from '../../hooks/useProjectUninvoicedHours';
import type {
  MonthlyUninvoicedBucket,
  UninvoicedKind,
} from '../../services/api/projectUninvoicedHours';
import { LEGACY_EN_NUMBER_LOCALE, formatNumber, numberFormatter } from '../../utils/localeFormat';

function fmtAmount(value: number, currency: string): string {
  try {
    return numberFormatter({
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }, LEGACY_EN_NUMBER_LOCALE).format(value);
  } catch {
    return `${formatNumber(value, { maximumFractionDigits: 2 }, LEGACY_EN_NUMBER_LOCALE)} ${currency}`;
  }
}

function periodRange(period: string): { from: string; to: string } | null {
  const m = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(period);
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const from = `${year}-${String(month).padStart(2, '0')}-01`;
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const to = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { from, to };
}

export interface UninvoicedHoursCardProps {
  projectId: number;
  /** From `Project.paymentTypeId`. */
  paymentTypeId: number | null;
  /** Constant from `frontend/src/constants/typeIds.ts`. */
  slaBalicekPaymentTypeId: number;
}

export function UninvoicedHoursCard({
  projectId,
}: UninvoicedHoursCardProps) {
  const { data, isLoading, isError } = useProjectUninvoicedHours(projectId, true);

  const summary = data?.data;
  const kind: UninvoicedKind = summary?.kind ?? 'hourly';
  const currency = summary?.currency ?? 'CZK';
  const byMonth = summary?.byMonth ?? [];
  const isOverage = kind === 'overage';

  const title = isOverage ? 'Overage hours (over package)' : 'Logged but not invoiced';
  const description = isOverage
    ? 'Hours over the monthly Hour Package, ordered FIFO by date the work was performed. Per-role billing uses each role’s overage rate.'
    : 'Approved & submitted hours on this project that are not yet attached to any invoice. Click Generate to open the invoice form prefilled with the project and the selected month.';

  return (
    <Card title={title}>
      <p className="mb-3 text-[11.5px] text-ms-text-muted">{description}</p>

      {isLoading && <div className="h-16 animate-pulse bg-ms-surface-2 rounded" />}

      {isError && !isLoading && (
        <p className="text-[12px] text-rose-700 italic">Failed to load uninvoiced hours.</p>
      )}

      {!isLoading && !isError && summary && (
        <>
          {isOverage && summary.hasZeroRateOverage && (
            <div className="mb-3 rounded-lg border border-rose-300 bg-rose-50 px-3 py-2">
              <p className="text-[11.5px] font-semibold text-rose-900">
                Overage hours without billing rate
              </p>
              <p className="mt-0.5 text-[11px] text-rose-800">
                Some overage rows below have no <code>ProjectOverageRate</code> set (or the entry
                pre-dates the role assignment). They will be invoiced at 0 CZK. Add a per-role rate
                in project settings before generating the invoice.
              </p>
            </div>
          )}

          <div className="mb-3 flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50 px-3 py-2">
            <span className="text-[11.5px] font-semibold uppercase tracking-wide text-primary-700">
              Total
            </span>
            <span className="text-[14px] font-bold tabular-nums text-primary-900">
              {summary.totalHours.toFixed(1)} h &middot; {fmtAmount(summary.totalBilling, currency)}
            </span>
          </div>

          {byMonth.length === 0 ? (
            <p className="text-[12px] text-ms-text-secondary italic">
              {isOverage
                ? 'No overage this period — every Hour Package month stays within the package.'
                : 'Nothing to invoice — every approved/submitted entry on this project is already on an invoice.'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[12.5px]">
                <thead>
                  <tr className="border-b border-ms-border text-left text-[10.5px] font-semibold uppercase tracking-wider text-ms-text-muted">
                    <th className="py-2 pr-3">Period</th>
                    <th className="py-2 pr-3 text-right">Hours</th>
                    <th className="py-2 pr-3 text-right">Billing</th>
                    {isOverage && <th className="py-2 pr-3">PM approval</th>}
                    <th className="py-2 pr-3 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {byMonth.map((row) => (
                    <MonthRow
                      key={row.period}
                      row={row}
                      projectId={projectId}
                      currency={currency}
                      isOverage={isOverage}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

interface MonthRowProps {
  row: MonthlyUninvoicedBucket;
  projectId: number;
  currency: string;
  isOverage: boolean;
}

function MonthRow({ row, projectId, currency, isOverage }: MonthRowProps) {
  const range = periodRange(row.period);
  const href = range
    ? `/invoices/new?projectIds=${projectId}&from=${range.from}&to=${range.to}`
    : null;
  const [expanded, setExpanded] = useState(false);
  const expandable = isOverage && row.byRole.length > 0;

  return (
    <>
      <tr className="border-b border-ms-border/60">
        <td className="py-2 pr-3 font-mono font-semibold tabular-nums">
          {expandable ? (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="inline-flex items-center gap-1 text-left hover:underline"
              aria-expanded={expanded}
            >
              <span aria-hidden="true" className="inline-block w-3 text-center">
                {expanded ? '−' : '+'}
              </span>
              {row.period}
            </button>
          ) : (
            row.period
          )}
        </td>
        <td className="py-2 pr-3 text-right tabular-nums">{row.hours.toFixed(1)}</td>
        <td className="py-2 pr-3 text-right tabular-nums">{fmtAmount(row.billing, currency)}</td>
        {isOverage && (
          <td className="py-2 pr-3">
            <ApprovalChip row={row} />
          </td>
        )}
        <td className="py-2 pr-3 text-right">
          {href ? (
            <Link
              to={href}
              className="inline-flex items-center rounded-md bg-primary-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-primary-700"
            >
              Generate
            </Link>
          ) : (
            <span className="text-[11px] text-ms-text-muted">&mdash;</span>
          )}
        </td>
      </tr>
      {expandable && expanded && (
        <tr className="border-b border-ms-border/60 bg-ms-surface-2">
          <td colSpan={5} className="py-2 px-3">
            <table className="w-full text-[11.5px]">
              <thead>
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wider text-ms-text-muted">
                  <th className="py-1 pr-3">Role</th>
                  <th className="py-1 pr-3 text-right">Hours</th>
                  <th className="py-1 pr-3 text-right">Rate</th>
                  <th className="py-1 pr-3 text-right">Billing</th>
                </tr>
              </thead>
              <tbody>
                {row.byRole.map((r) => (
                  <tr key={`${row.period}-${r.roleId ?? 'none'}`}>
                    <td className="py-1 pr-3">
                      {r.roleName ?? <span className="italic text-ms-text-muted">(no role)</span>}
                      {!r.hasRate && (
                        <span className="ml-2 inline-flex items-center rounded bg-rose-100 px-1.5 py-0.5 text-[9.5px] font-semibold text-rose-800">
                          Rate not set
                        </span>
                      )}
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums">{r.hours.toFixed(1)}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">
                      {r.hasRate ? fmtAmount(r.hourlyRate, currency) : <span className="text-ms-text-muted">&mdash;</span>}
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums">
                      {fmtAmount(r.billing, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function ApprovalChip({ row }: { row: MonthlyUninvoicedBucket }) {
  const approval = row.approval;
  if (!approval) return <span className="text-ms-text-muted">&mdash;</span>;
  if (approval.status === 'ok') {
    return (
      <span className="inline-flex items-center rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800">
        Approved {approval.approvedHours?.toFixed(1) ?? '0'} h
      </span>
    );
  }
  if (approval.status === 'less') {
    return (
      <span
        className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800"
        title={`PM approved ${approval.approvedHours?.toFixed(1) ?? '0'} h, but actual overage is ${row.hours.toFixed(1)} h.`}
      >
        PM approved {approval.approvedHours?.toFixed(1) ?? '0'} h &lt; actual {row.hours.toFixed(1)} h
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-700">
      No PM approval
    </span>
  );
}
